"use client";

import { useEffect, useState, useCallback } from "react";
import { useParams, useRouter } from "next/navigation";
import { Button } from "@/components/ui/button";
import { studyUrl } from "@/lib/study-mode";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/cn";
import { ONBOARDING_STORAGE_KEY } from "@/components/dashboard/GettingStartedChecklist";
import type { Database } from "@/lib/supabase/types";
import { trackDeckExported, trackExportFailed, trackLimitHit, trackUpgradePromptShown } from "@/lib/analytics";
import { canExportAnki } from "@/lib/billing/plans";
import { UpgradeModal } from "@/components/UpgradeModal";
import type { SubscriptionTier } from "@/lib/constants";

type Deck = Database["public"]["Tables"]["decks"]["Row"];
type Card = Database["public"]["Tables"]["cards"]["Row"];

type ExportState = "idle" | "generating" | "done" | "error";

function markExportComplete() {
  try {
    const raw = localStorage.getItem(ONBOARDING_STORAGE_KEY);
    const state = raw ? JSON.parse(raw) : { dismissed: false, completed: {} };
    state.completed.exported = true;
    localStorage.setItem(ONBOARDING_STORAGE_KEY, JSON.stringify(state));
  } catch {}
}

export default function ExportPage() {
  const { deckId } = useParams<{ deckId: string }>();
  const router = useRouter();

  const [deck, setDeck] = useState<Deck | null>(null);
  const [cards, setCards] = useState<Card[]>([]);
  const [loading, setLoading] = useState(true);
  const [ankiState, setAnkiState] = useState<ExportState>("idle");
  const [csvState, setCsvState] = useState<ExportState>("idle");
  const [tier, setTier] = useState<SubscriptionTier>("free");
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  useEffect(() => {
    async function fetchDeck() {
      const [deckRes, profileRes] = await Promise.all([
        fetch(`/api/decks/${deckId}`),
        fetch("/api/settings/profile"),
      ]);
      if (!deckRes.ok) {
        router.push("/dashboard");
        return;
      }
      const data = await deckRes.json();
      setDeck(data.deck);
      setCards(data.cards);
      if (profileRes.ok) {
        const profile = await profileRes.json();
        setTier(profile.subscription_tier ?? "free");
      }
      setLoading(false);
    }
    fetchDeck();
  }, [deckId, router]);

  const handleCsvExport = useCallback(async () => {
    if (!deck) return;
    setCsvState("generating");
    try {
      const res = await fetch(`/api/export/csv/${deckId}`);
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${deck.title.replace(/[^a-zA-Z0-9]/g, "_")}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      setCsvState("done");
      trackDeckExported(deckId, "csv", cards.length);
      markExportComplete();
    } catch (e) {
      setCsvState("error");
      trackExportFailed(deckId, "csv", e instanceof Error ? e.message : "CSV export failed");
    }
  }, [deck, deckId, cards]);

  const handleAnkiExport = useCallback(async () => {
    if (!deck || cards.length === 0) return;
    setAnkiState("generating");
    try {
      // Dynamic import to avoid loading WASM until needed
      const { generateApkg } = await import("@/lib/export/anki");
      const data = await generateApkg(
        deck.title,
        cards.map((c) => ({ question: c.question, answer: c.answer }))
      );
      const blob = new Blob([data.buffer as ArrayBuffer], { type: "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${deck.title.replace(/[^a-zA-Z0-9]/g, "_")}.apkg`;
      a.click();
      URL.revokeObjectURL(url);
      setAnkiState("done");
      trackDeckExported(deckId, "anki", cards.length);
      markExportComplete();
    } catch (e) {
      setAnkiState("error");
      trackExportFailed(deckId, "anki", e instanceof Error ? e.message : "Anki export failed");
    }
  }, [deck, cards]);

  const handleLockedAnkiClick = () => {
    trackLimitHit("anki_export", tier);
    trackUpgradePromptShown("export");
    setUpgradeOpen(true);
  };

  if (loading || !deck) {
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="flex items-center justify-center gap-3 mb-2">
          <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
          <p className="font-display text-xs font-bold uppercase tracking-widest text-border-thick/50">Loading export...</p>
        </div>
        <div className="skeleton-shimmer rounded-xl border-2 border-border-thick/20 h-32 mx-auto max-w-xs" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="skeleton-shimmer rounded-xl border-2 border-border-thick/20 h-40" />
          <div className="skeleton-shimmer rounded-xl border-2 border-border-thick/20 h-40" style={{ animationDelay: "0.15s" }} />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="text-center mb-10">
        <Badge variant="success" className="mb-4">Export Ready</Badge>
        <h1 className="font-display text-4xl md:text-5xl font-bold leading-none tracking-tight uppercase">
          Export <span className="text-primary">Deck</span>
        </h1>
        <p className="font-body text-sm text-border-thick/60 mt-3">
          {deck.title} — {cards.length} cards
        </p>
      </div>

      {/* Export options */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
        {/* Anki export */}
        {!canExportAnki(tier) ? (
          <div
            onClick={handleLockedAnkiClick}
            className={cn(
              "relative flex flex-col items-center gap-3 p-8 bg-white border-2 border-border-thick/40 rounded-xl",
              "cursor-default opacity-70"
            )}
          >
            <div className="w-12 h-12 flex items-center justify-center bg-border-thick/5 border-2 border-border-thick/20 rounded-lg">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-border-thick/40">
                <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            </div>
            <div className="text-center">
              <p className="font-display text-base font-bold">Export for Anki</p>
              <p className="font-body text-xs text-border-thick/40 mt-1">Pro feature</p>
            </div>
            <Button
              onClick={(e) => { e.stopPropagation(); trackUpgradePromptShown("export"); setUpgradeOpen(true); }}
              size="sm"
              className="mt-1"
            >
              Upgrade to Pro
            </Button>
          </div>
        ) : (
          <button
            onClick={handleAnkiExport}
            disabled={ankiState === "generating"}
            className={cn(
              "flex flex-col items-center gap-3 p-8 bg-white border-2 border-border-thick rounded-xl shadow-brutal-sm",
              "hover:shadow-brutal hover:-translate-x-[2px] hover:-translate-y-[2px]",
              "active:translate-x-[2px] active:translate-y-[2px] active:shadow-none",
              "transition-all duration-100 cursor-pointer",
              "disabled:opacity-50 disabled:cursor-not-allowed",
              ankiState === "done" && "border-success bg-success/10"
            )}
          >
            <div className="w-12 h-12 flex items-center justify-center bg-primary/10 border-2 border-primary/20 rounded-lg">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" x2="12" y1="15" y2="3" />
              </svg>
            </div>
            <div className="text-center">
              <p className="font-display text-base font-bold">Export for Anki</p>
              <p className="font-body text-xs text-border-thick/50 mt-1 flex items-center justify-center gap-1.5">
                {ankiState === "generating" && (
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                )}
                {ankiState === "generating"
                  ? "Generating .apkg..."
                  : ankiState === "done"
                    ? "Downloaded!"
                    : ankiState === "error"
                      ? "Export failed — try again"
                      : "Download .apkg for desktop"}
              </p>
              {ankiState === "generating" && (
                <p className="font-body text-xs text-border-thick/30 mt-1">This may take a few seconds</p>
              )}
            </div>
          </button>
        )}

        {/* CSV export */}
        <button
          onClick={handleCsvExport}
          disabled={csvState === "generating"}
          className={cn(
            "flex flex-col items-center gap-3 p-8 bg-white border-2 border-border-thick rounded-xl shadow-brutal-sm",
            "hover:shadow-brutal hover:-translate-x-[2px] hover:-translate-y-[2px]",
            "active:translate-x-[2px] active:translate-y-[2px] active:shadow-none",
            "transition-all duration-100 cursor-pointer",
            "disabled:opacity-50 disabled:cursor-not-allowed",
            csvState === "done" && "border-success bg-success/10"
          )}
        >
          <div className="w-12 h-12 flex items-center justify-center bg-safety-orange/10 border-2 border-safety-orange/20 rounded-lg">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-safety-orange">
              <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" /><path d="M14 2v4a2 2 0 0 0 2 2h4" /><path d="M10 13H8" /><path d="M16 17H8" /><path d="M16 13h-2" />
            </svg>
          </div>
          <div className="text-center">
            <p className="font-display text-base font-bold">Export as CSV</p>
            <p className="font-body text-xs text-border-thick/50 mt-1 flex items-center justify-center gap-1.5">
              {csvState === "generating" && (
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
              )}
              {csvState === "generating"
                ? "Generating..."
                : csvState === "done"
                  ? "Downloaded!"
                  : csvState === "error"
                    ? "Export failed — try again"
                    : "Simple spreadsheet format"}
            </p>
          </div>
        </button>
      </div>

      {/* Secondary actions */}
      <div className="flex flex-wrap justify-center gap-4 pt-6 border-t border-retro-gray">
        <Button variant="ghost" size="sm" onClick={() => router.push(studyUrl(deckId))}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1.5"><polygon points="6 3 20 12 6 21 6 3" /></svg>
          Start Studying
        </Button>
        <Button variant="ghost" size="sm" onClick={() => router.push(`/deck/${deckId}`)}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1.5"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" /></svg>
          Edit Deck
        </Button>
        <Button variant="ghost" size="sm" onClick={() => router.push("/upload")}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1.5"><path d="M5 12h14" /><path d="M12 5v14" /></svg>
          Create Another
        </Button>
      </div>

      <UpgradeModal
        open={upgradeOpen}
        onClose={() => setUpgradeOpen(false)}
        location="export"
      />
    </div>
  );
}
